<template>
    <div class="w-full mb-3 flex flex-row gap-2">
        <Button label="Сбросить" @click="clear()" icon="pi pi-refresh" iconPos="right" severity="secondary" class="p-button-sm border-round-md border-0 text-xs font-medium"/>
    </div>
    <div class="w-full flex flex-column m-0 p-0">
        <DataTable v-if="operations" :value="operations" lazy paginator :rows="20" v-model:filters="filters" ref="dt" dataKey="id"
            :totalRecords="totalRecords" :loading="loading" @page="onPage($event)" @sort="onSort($event)"
            @filter="onFilter($event)" filterDisplay="menu" 
            @row-select="onRowSelect" @row-unselect="onRowUnselect" 
            :rowsPerPageOptions="[5, 10, 20, 50, 100, 200, 300, 400, 500]">

            <Column field="_id" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
                <template #header>
                    <span class="text-xs font-medium">ID</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div class="flex flex-row gap-1 text-xs vertical-align-middle justify-content-start align-items-center">
                        <span class="flex my-auto overflow-hidden" style="max-width:5em">
                            <span class="white-space-nowrap" style="text-overflow:ellipsis;">
                                {{ data.id }}
                            </span>
                        </span>...
                        <Button @click="copy(data.id)" icon="pi pi-copy" iconPos="right" size="small" outlined severity="secondary" class="border-0" />
                    </div> 
                </template>
            </Column>

            <Column field="status" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :showApplyButton="false">
                <template #header>
                    <span class="text-xs font-medium">Статус</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <Dropdown v-model="filterModel.value" @change="filterCallback()" :options="statuses" placeholder="Все" class="p-column-filter" :showClear="false">
                        <template #option="slotProps">
                            <Tag :value="getStatusTitle(slotProps.option)" :severity="getStatusSeverity(slotProps.option)" class="ml-2 text-xs"/>
                        </template>
                    </Dropdown>
                </template>
                <template #body="{ data }">
                    <Tag :value="getStatusTitle(data.status)" :severity="getStatusSeverity(data.status)" class="ml-2 text-xs"/>
                </template>
            </Column>

            <Column field="step" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true" :showApplyButton="false" >
                <template #header>
                    <span class="text-xs font-medium">Шаг</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <Dropdown v-model="filterModel.value" @change="filterCallback()" :options="steps" placeholder="Все" class="p-column-filter" :showClear="false">
                        <template #option="slotProps">
                            <Tag :value="getStatusTitle(slotProps.option)" :severity="getStatusSeverity(slotProps.option)" class="ml-2 text-xs"/>
                        </template>
                    </Dropdown>
                </template>
                <template #body="{ data }">
                    <Tag :value="getStatusTitle(data.step)" :severity="getStatusSeverity(data.step)" class="ml-2 text-xs"/>
                </template>
            </Column>

            <Column field="items">
                <template #header>
                    <span class="text-xs font-medium">Сумма</span>
                </template>
                <template #body="{ data }">
                    <span v-if="data.items && data.items.length" class="white-space-nowrap text-">
                        <span>{{ getFormatAmount(data.items[0].amount, data.items[0].currency) }}</span>
                    </span>
                </template>
            </Column>

            <Column field="description">
                <template #header>
                    <span class="text-xs font-medium">Описание</span>
                </template>
                <template #body="{ data }">
                    <div v-if="data.description" class="flex flex-row gap-2 text-xs white-space-nowrap vertical-align-middle justify-content-start">
                        <span class="flex flex-row gap-2 text-xs white-space-nowrap vertical-align-middle justify-content-start">
                            <span class="flex my-auto" v-tooltip="{ value: data.description }">{{ getShortDescription(data.description) }}</span>
                        </span>
                        <Button @click="copy(data.description)" icon="pi pi-copy" iconPos="right" size="small" outlined severity="secondary" class="border-0" />
                    </div>
                </template>
            </Column>

            <Column field="error_message" header="" filterMatchMode="startsWith" >
                <template #header>
                    <span class="text-xs font-medium">Ошибка</span>
                </template>
                <template #body="{ data }">
                    <div v-if="data.error_message" class="flex flex-row gap-2 text-xs white-space-nowrap vertical-align-middle justify-content-start">
                        <span class="flex flex-row gap-2 text-xs white-space-nowrap vertical-align-middle justify-content-start">
                            <span class="flex my-auto" v-tooltip="{ value: data.error_message }">{{ getShortDescription(data.error_message) }}</span>
                        </span>
                        <Button @click="copy(data.error_message)" icon="pi pi-copy" iconPos="right" size="small" outlined severity="secondary" class="border-0" />
                    </div>
                </template>
            </Column>

            <Column field="updated" header="" filterMatchMode="startsWith" >
                <template #header>
                    <span class="text-xs font-medium">Действия</span>
                </template>
                <template #body="{ data }">
                    <div v-if="data.status == 'pending'"
                        class="flex flex-row gap-2">
                        <Button label="Подтвердить" @click="CommitDialog(data.id)" :loading="loading"
                            icon="pi pi-check" iconPos="right" severity="success" 
                            class="p-button-sm border-round-md border-0 text-xs font-medium"/>
                        <Button label="" @click="RevertDialog(data.id)" :loading="loading"
                            icon="pi pi-undo" iconPos="right" severity="danger" 
                            class="p-button-sm border-round-md border-0 text-xs font-medium"/>
                    </div>
                </template>
            </Column>

            <!-- <Column field="user_id" filterMatchMode="contains" :showFilterMatchModes="false" :showClearButton="true">
                <template #header>
                    <span class="text-xs font-medium">user_id</span>
                </template>
                <template #filter="{ filterModel, filterCallback }">
                    <InputText type="text" v-model="filterModel.value" @keydown.enter="filterCallback()" class="p-column-filter w-12rem" placeholder="Найти" />
                </template>
                <template #body="{ data }">
                    <div v-if="data.user_id" class="flex flex-row gap-1 text-xs vertical-align-middle justify-content-start align-items-center">
                        <span class="flex my-auto overflow-hidden cursor-pointer" style="max-width:5em" @click="goTo(data.user_id, 'user')">
                            <span class="white-space-nowrap cursor-pointer text-primary" style="text-overflow:ellipsis;">
                                {{ data.user_id }}
                            </span>
                        </span>...
                        <div class="flex flex-row gap-1">
                            <Button @click="copy(data.user_id)" icon="pi pi-copy" iconPos="right" size="small" outlined severity="secondary" class="border-0" />
                            <Button @click="goTo(data.user_id, 'user', true)" icon="pi pi-external-link" iconPos="right" size="small" outlined severity="info" class="border-0" />
                        </div>
                    </div> 
                </template>
            </Column> -->

            <Column field="created" header="" filterMatchMode="startsWith" >
                <template #header>
                    <span class="text-xs font-medium">Создан</span>
                </template>
                <template #body="{ data }">
                    <span class="flex text-xs">{{formatDate(data.created)}}</span>
                </template>
            </Column>

            <Column field="updated" header="" filterMatchMode="startsWith" >
                <template #header>
                    <span class="text-xs font-medium">Обновлен</span>
                </template>
                <template #body="{ data }">
                    <span class="flex text-xs">{{formatDate(data.updated)}}</span>
                </template>
            </Column>

            <template #footer>
                <span class="text-sm text-600 font-medium">
                    Всего {{ totalRecords }} операций 
                </span>
            </template>
        
        </DataTable>
    </div>

    <Dialog class="flex flex-column border-none" modal
        v-model:visible="dialogTx"
        :position="screenSize=='xs' ? 'bottom' : 'center'"
        :style="(screenSize=='xs' || screenSize=='sm' || screenSize=='md') ? {width: '100%!important', height: '100%!important', margin: '0!important', padding: '0!important'} : { width: '100%!important' }" 
        :contentStyle="(screenSize=='xs' || screenSize=='sm' || screenSize=='md') ? { height: '100%' } : { height: 'auto' }"
        :class="(screenSize=='xs') ? 'h-full border-noround-bottom' : ''">

        <template #header>
            <div class="flex flex-column w-full mb-3">
                <span class="flex text-lg font-medium">Проводки</span>
            </div>
        </template>

        <div class="w-full mb-3">
            <OperationsTxList :items="txList" :currencies="currencies"/>
        </div>
    </Dialog>
</template>

<script>
import Connect from '@/assets/js/connect.js'
import Helper from '@/assets/js/helper.js'
import OperationsTxList from '@/components/admin/operation/op_items.vue'

export default {
    props: {
        operations: {
            type: Array,
            required: true
        },
        totalRecords: {
            type: Number,
            required: true
        },
        loading: {
            type: Boolean,
            required: true
        },
        defaultParams: {
            type: Object,
            required: true
        }
    },
    components: {
        OperationsTxList
    },
    data() {
        return {
            currencies: [
                {
                    code: 'usdt',
                    name: 'Tether',
                    image: '/images/currencies/usdt.png'
                },
                {
                    code: 'rub',
                    name: 'Russian Ruble',
                    image: '/images/currencies/rub.png'
                },
            ],
            currenciesCodes: [],
            dialogTx: false,
            txList: [],
            sort: {
                field: 'created_at',
                order: -1
            },
            lazyParams: this.defaultParams,
            filters: {
                '_id': { value: null, matchMode: 'contains' },
                'user_id': { value: null, matchMode: 'contains' },
                'status': { value: null, matchMode: 'contains' },
                'step': { value: null, matchMode: 'contains' },
                'type': { value: null, matchMode: 'contains' },
                'transfer_id': { value: null, matchMode: 'contains' },
                'external_source': { value: null, matchMode: 'contains' },
                'hash': { value: null, matchMode: 'contains' },
            },
            statuses: [
                'pending', 'processing', 'failed', 'completed'
            ],
            steps: [
                'created', 'processing', 'completed', 'failed', 'canceled'
            ],
            types: [
                'virtual_withdraw',
                'onchain_withdraw',
                'virtual_deposit',
                'onchain_deposit',
                'virtual_transfer',
                'onchain_sweep',
                'virtual_multiple_transfer',
                'issuing_vcard',
                'topup_vcard',
                'squarefi_deposit',
            ],
            sources: ['internal', 'squarefi', 'another'],
            networks: [
                'bep20', 'trc20',
            ],
            cryptoCurrencies: [
                'usdt', 'usdt_trc'
            ]
        }
    },
    computed: {
        screenSize() { return this.$store.getters.StateScreenSize },
    },
    methods: {
        copy(text){
            this.helper.copy_clipboard(text)
            this.$toast.add({ severity: 'info', summary: 'Скопировано', detail: text, life: 3000 })
        },
        getFormatAmount(value, currency, digits=2) {
            return this.helper.format_amount(value, currency, digits)
        },
        goToTransactions(id){
            if(!id) return
            window.open(`/admin/transactions/?operation_id=${id}`, '_blank')
        },
        goTo(id, type, new_tab = false) {
            if(!id) return
            if(!type) return
            if(new_tab){
                const routeData = this.$router.resolve({path: '/admin/'+type+'/' + id});
                window.open(routeData.href, '_blank');
            } else  this.$router.push({ path: '/admin/'+type+'/' + id })
        },
        goto_address(address, network='bep20'){
            if(!address || !network) return
            const prod = (process.env.VUE_APP_MODE == 'production') ? true : false
            const bsc = (prod) ? 'https://bscscan.com/address/' : 'https://testnet.bscscan.com/address/'
            const eth = (prod) ? 'https://etherscan.io/address/' : 'https://testnet.etherscan.io/address/'
            if(network == 'eth'){
                window.open(eth+address, '_blank');
            } else if(network == 'bep20'){
                window.open(bsc+address, '_blank');
            }
        },
        goToHashBlock(hash, network){
            if(!hash || !network) return
            const test = (process.env.VUE_APP_MODE == 'production') ? false : true
            const url = this.helper.GetScanHashUrl(hash, network, test)
            if(!url) return
            window.open(url, '_blank');
        },
        getClassByLabel(label){
            return this.helper.GetClassByLabel(label)
        },
        getSysWallet(value){
            if(!value) return null
            if(value == this.wallet_id) return 'current'
            return this.helper.GetSystemWalletName(value, this.wallet_id, this.systemWallets)
        },
        getSysAccount(value){
            if(!value) return null
            if(value == this.account_id) return 'current'
            return this.helper.GetSystemAccountName(value, this.account_id, this.systemWallets)
        },
        getSeverityByType(type) {
            if(type == 'onchain_sweep') return 'info'
            if(type == 'onchain_deposit') return 'success'
            if(type == 'onchain_withdraw') return 'warn'
            if(type == 'issuing_vcard') return 'danger'
            return 'secondary'
        },
        getShortDescription(value){
            if(!value) return ''
            return this.helper.get_short_name(value, 25)
        },
        getCurImage(code){
            return this.helper.GetCurrencyImage(code, this.currencies)
        },
        getNetworkImage(network) {
            if(!network) return null
           return this.helper.GetNetworkImage(network)
        },
        getStatusSeverity(status) {
            return this.helper.getStatusSeverity(status)
        },
        getStatusTitle(status) {
            return this.helper.getStatusTitle(status)
        },
        formatDate(value) {
            return this.helper.format_date(value)
        },
        getShortAddress(value){
            if(!value) return ''
            return this.helper.get_short_name(value)
        },
        getShortId(value){
            return this.helper.get_short_id(value)
        },
        showTx(items) {
            this.dialogTx = true
            this.txList = items ? items : []
        },
        onPage(event) {
            this.lazyParams = event
            this.$emit('onPageChange', this.lazyParams)
        },
        onSort(event) {
            if(event.sortField && event.sortOrder){
                this.sort = {
                    field: event.sortField,
                    order: event.sortOrder
                }
            } else {
                this.sort = {
                    field: 'created_at',
                    order: -1
                }
            }
        },
        onFilter() {
            this.$emit('onFilter', this.filters)
        },
        onRowSelect() {
            this.selectAll = this.selectedCustomers.length === this.totalRecords
        },
        onRowUnselect() {
            this.selectAll = false;
        },
        clear() {
            this.filters = {
                '_id': { value: null, matchMode: 'contains' },
                'user_id': { value: null, matchMode: 'contains' },
                'status': { value: null, matchMode: 'contains' },
                'step': { value: null, matchMode: 'contains' },
                'type': { value: null, matchMode: 'contains' },
                'transfer_id': { value: null, matchMode: 'contains' },
                'external_source': { value: null, matchMode: 'contains' },
                'hash': { value: null, matchMode: 'contains' },
            }
            this.onFilter()
        },
        GetCurrencyList(){
            const objList = this.$store.state.payment.currencies
            if(!objList)return
            let currencies = []
            for(const [k,v] of Object.entries(objList)){
                currencies.push({
                    code:   v.code,
                    name:   v.name,
                    image:  v.image
                })
            }
            this.currencies = currencies
            this.currenciesCodes = Object.keys(objList)
        },
        CommitDialog(operationId, data = {}) {
            this.$confirm.require({
                message: 'Подтвердить операцию?',
                header: 'Подтверждение',
                icon: 'pi pi-exclamation-triangle',
                rejectProps: {
                    label: 'Отменить',
                    severity: 'secondary',
                    outlined: true
                },
                acceptProps: {
                    label: 'Подтвердить',
                    severity: 'success'
                },
                accept: () => this.Commit(operationId, data),
                reject: () => {
                    // this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
                }
            });
        },
        RevertDialog(operationId, data = {}) {
            this.$confirm.require({
                message: 'Отменить операцию?',
                header: 'Подтверждение',
                icon: 'pi pi-exclamation-triangle',
                rejectProps: {
                    label: 'Отменить',
                    severity: 'secondary',
                    outlined: true
                },
                acceptProps: {
                    label: 'Отменить операцию',
                    severity: 'danger'
                },
                accept: () => this.Revert(operationId, data),
                reject: () => {
                    // this.$toast.add({ severity: 'error', summary: 'Rejected', detail: 'You have rejected', life: 3000 });
                }
            });
        },
        async Commit(operationId, data = {}){
            this.$emit('loading', true)
            try {
                const res = await this.connect.CommitTransferWalletByAdmin(operationId, data)
                if(res && res.result){
                    this.$toast.add({ severity: 'success', summary: 'Успешно', detail: 'Операция подтверждена', life: 2000 })
                    this.$emit('onPageChange', this.lazyParams)
                } else {
                    if(res.message) this.showMess('warn', 'Ошибка', res.message)
                    else this.showMess('error', 'Ошибка', (res.error) ? res.error : 'Ошибка сервера')
                }
            } catch (e) {
                this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Сеть недоступна или внутренняя ошибка', life: 2000 })
            } finally {
                this.$emit('loading', false)
            }
        },
        async Revert(operationId, data = {}){
            this.$emit('loading', true)
            try {
                const res = await this.connect.RevertTransferWalletByAdmin(operationId, data)
                if(res && res.result){
                    this.$toast.add({ severity: 'success', summary: 'Успешно', detail: 'Операция отменена', life: 2000 })
                    this.$emit('onPageChange', this.lazyParams)
                } else {
                    if(res.message) this.showMess('warn', 'Ошибка', res.message)
                    else this.showMess('error', 'Ошибка', (res.error) ? res.error : 'Ошибка сервера')
                }
            } catch (e) {
                this.$toast.add({ severity: 'error', summary: 'Ошибка', detail: 'Сеть недоступна или внутренняя ошибка', life: 2000 })
            } finally {
                this.$emit('loading', false)
            }
        }
    },
    beforeCreate() {
        this.connect = new Connect()
        this.helper = new Helper()
    },
    mounted() {
        // this.GetCurrencyList()
    }
}
</script>